import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';

const ServicesHero = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"]
  });

  // Parallax transforms - DESKTOP ONLY
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.5], [1, 0.8, 0]);
  const y = useTransform(scrollYProgress, [0, 0.5], [0, -100]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);
  const glowIntensity = useTransform(scrollYProgress, [0, 0.3], [0.05, 0.15]);

  return (
    <section 
      ref={sectionRef}
      className="relative bg-dark-bg min-h-[85vh] lg:min-h-[100vh] flex items-center justify-center overflow-hidden"
    >
      {/* Continuous Background Grid */}
      <div className="absolute inset-0 pointer-events-none">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(rgba(20, 184, 166, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(20, 184, 166, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      {/* Dynamic Background Glows - DESKTOP ONLY */}
      <motion.div 
        className="hidden lg:block absolute top-20 left-10 w-96 h-96 bg-accent-blue rounded-full blur-3xl pointer-events-none"
        style={{ opacity: glowIntensity }}
      />
      <motion.div 
        className="hidden lg:block absolute bottom-20 right-10 w-96 h-96 bg-accent-blue rounded-full blur-3xl pointer-events-none"
        style={{ opacity: glowIntensity }}
      />

      {/* Static Background Glows - MOBILE */}
      <div className="lg:hidden absolute top-20 left-10 w-96 h-96 bg-accent-blue/5 rounded-full blur-3xl pointer-events-none" />
      <div className="lg:hidden absolute bottom-20 right-10 w-96 h-96 bg-accent-blue/5 rounded-full blur-3xl pointer-events-none" />

      {/* DESKTOP - Scroll-animated content */}
      <motion.div 
        style={{ opacity, y, scale }}
        className="hidden lg:block relative z-10 max-w-[1200px] mx-auto px-6 md:px-8 text-center"
      >
        {/* Eyebrow */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-sm md:text-base text-accent-blue font-semibold mb-4 md:mb-6 uppercase tracking-wider"
        >
          Services
        </motion.p>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white mb-6 md:mb-8 leading-tight"
        >
          Identify and remove
          <br className="hidden md:block" />
          <span className="text-accent-blue"> growth bottlenecks</span>
        </motion.h1>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-base md:text-lg lg:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed mb-10 md:mb-12"
        >
          From one-time audits to ongoing advisory, we help you find and remove the structural constraints blocking growth
        </motion.p>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <button
            onClick={() => window.REACT_APP_NAVIGATE('/apply')}
            className="bg-accent-blue text-white px-8 md:px-10 lg:px-12 py-4 md:py-5 rounded-full text-base md:text-lg font-semibold hover:bg-blue-600 transition-all duration-300 shadow-lg shadow-accent-blue/20 hover:shadow-accent-blue/40 hover:scale-105 whitespace-nowrap min-h-[56px] inline-flex items-center justify-center cursor-pointer"
          >
            Get a Growth Bottleneck Audit
          </button>
        </motion.div>
      </motion.div>

      {/* MOBILE - Simple fade-in content, NO PARALLAX */}
      <div className="lg:hidden relative z-10 max-w-[1200px] mx-auto px-5 text-center py-20">
        {/* Eyebrow */}
        <motion.p
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6 }}
          className="text-[13px] text-accent-blue font-semibold mb-5 uppercase tracking-widest"
        >
          Services
        </motion.p>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-[36px] leading-[1.15] font-bold text-white mb-6"
        >
          Identify and remove
          <br />
          <span className="text-accent-blue"> growth bottlenecks</span>
        </motion.h1>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-[17px] leading-[1.7] text-gray-400 max-w-3xl mx-auto mb-8"
        >
          From one-time audits to ongoing advisory, we help you find and remove the structural constraints blocking growth
        </motion.p>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <button
            onClick={() => window.REACT_APP_NAVIGATE('/apply')}
            className="w-full bg-accent-blue text-white px-7 py-4 rounded-full text-[17px] font-semibold transition-all duration-300 shadow-lg shadow-accent-blue/20 whitespace-nowrap min-h-[58px] inline-flex items-center justify-center cursor-pointer"
          >
            Get a Growth Bottleneck Audit
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesHero;
